import {
  ScanLocationSection,
  ProductsToLocateHeader,
  ProductsToLocateSection,
  LocatedProductsSection,
  LocateFinishedMessage,
} from '../display/locate';
import { LocateActionButtons } from '../interaction';
import { Breadcrumb } from '../display';
import { LoadingState } from '../feedback';
import { useLocateLogic, useProductLocations, useReceiveReturned, useRegisteredData } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect, useRef } from 'react';

interface LocateContainerProps {
  returnedId: number;
}

export const LocateContainer = ({
  returnedId,
}: LocateContainerProps) => {
  const { t } = useTranslation('receiveModule');
  const navigate = useNavigate();

  // 🎯 Obtener datos del rehusado
  const { returnedData, orders, isLoading, ordersLoading } =
    useReceiveReturned(returnedId);



  // 🎯 Hook para datos registrados
  const { sortedRegisteredProducts } = useRegisteredData({
    shouldShowRegisteredData: true, // ✅ Siempre true para LocateContainer
    returnedData,
    orders,
    sortProducts: (products: any[], sortBy: string) => {
      return [...products].sort((a, b) => {
        switch (sortBy) {
          case 'more_qty': {
            const aQty = (a as any).processedQuantity || 0;
            const bQty = (b as any).processedQuantity || 0;
            return bQty - aQty;
          }
          case 'less_qty': {
            const aQty = (a as any).processedQuantity || 0;
            const bQty = (b as any).processedQuantity || 0;
            return aQty - bQty;
          }
          case 'name_az':
            return a.name.localeCompare(b.name);
          case 'name_za':
            return b.name.localeCompare(a.name);
          default:
            return 0;
        }
      });
    },
    sortOrders: () => [],
    productsSortBy: 'name_az',
    ordersSortBy: 'name_az',
  });

  // 🎯 Preparar datos de productos para pasar al hook de lógica
  const processedProducts = sortedRegisteredProducts;
  const processedProductsQuantities = sortedRegisteredProducts.reduce(
    (acc, product) => {
      acc[product.code] = product.processedQuantity || 0;
      return acc;
    },
    {} as Record<string, number>
  );
  const processedProductsTotals = sortedRegisteredProducts.reduce(
    (acc, product) => {
      acc[product.code] = product.totalQuantity || 0;
      return acc;
    },
    {} as Record<string, number>
  );



  // 🎯 Obtener IDs de productos para consultar sus ubicaciones
  const productIds =
    processedProducts
      ?.map((product) => parseInt(product.id))
      .filter((id) => !isNaN(id)) || [];

  // 🎯 Hook para obtener ubicaciones de productos
  const { productLocationMap } = useProductLocations(productIds);

  // 🎯 Hook principal con toda la lógica de ubicación
  const {
    productsToLocate,
    locatedProducts,
    toLocateSortBy,
    locatedSortBy,
    currentLocation,
    isValidatingLocation,
    productBeingLocated,
    quantityToLocate,
    setQuantityToLocate,
    handleLocationScan,
    handleScanUnits,
    handleProductLocate,
    handleClearLocation,
    handleToLocateSort,
    handleLocatedSort,
    handleFinishLocated,
    isLocating,
  } = useLocateLogic({
    returnedId,
    processedProducts,
    processedProductsQuantities,
    processedProductsTotals,
  });

  // 🎯 Estado de búsqueda
  const [toLocateSearch, setToLocateSearch] = useState('');
  const [locatedSearch, setLocatedSearch] = useState('');

  // 🎯 Listas filtradas por búsqueda (name/code)
  const filteredProductsToLocate = useMemo(() => {
    const q = toLocateSearch.toLowerCase().trim();
    if (!q) return productsToLocate;
    return productsToLocate.filter(
      (p: any) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q)
    );
  }, [productsToLocate, toLocateSearch]);

  const filteredLocatedProducts = useMemo(() => {
    const q = locatedSearch.toLowerCase().trim();
    if (!q) return locatedProducts;
    return locatedProducts.filter(
      (p: any) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q)
    );
  }, [locatedProducts, locatedSearch]);

  // 🎯 Estado de carga
  const expectedOrdersCount = Array.from(
    new Set((returnedData?.lines || []).map((l: any) => l?.order_id).filter(Boolean))
  ).length;
  const shouldWaitOrders = expectedOrdersCount > 0 && (orders?.length || 0) < expectedOrdersCount;

  // 🎯 Estado de carga - Aplicar opacidad en lugar de spinner
  const isLoadingData = isLoading || ordersLoading || shouldWaitOrders || !returnedData;

  // 🎯 Ref para rastrear si había productos inicialmente cuando se cargaron los datos
  const hadProductsInitiallyRef = useRef<number | null>(null);
  // 🎯 Ref para evitar que se ejecute múltiples veces el auto-finish
  const hasAutoFinishedRef = useRef(false);

  // 🎯 Rastrear si había productos inicialmente cuando se cargaron los datos
  useEffect(() => {
    // Solo establecer el valor inicial una vez cuando los datos se cargan completamente
    // y hay productos por ubicar
    if (!isLoadingData && hadProductsInitiallyRef.current === null) {
      hadProductsInitiallyRef.current = productsToLocate.length;
    }
  }, [isLoadingData, productsToLocate.length]);

  // 🎯 Resetear los refs cuando cambia el returnedId
  useEffect(() => {
    hadProductsInitiallyRef.current = null;
    hasAutoFinishedRef.current = false;
  }, [returnedId]);

  // 🎯 Auto-finalizar cuando aparece el mensaje "Ubicar finalizado"
  // Se ejecuta SOLO cuando el usuario ubicó todos los productos durante esta sesión
  useEffect(() => {
    // Solo ejecutar si:
    // 1. Los datos ya se cargaron completamente (!isLoadingData)
    // 2. No está en proceso de ubicación (!isLocating)
    // 3. No hay productos por ubicar (productsToLocate.length === 0)
    // 4. Hubo productos inicialmente cuando se cargaron los datos (hadProductsInitiallyRef.current > 0)
    // 5. El usuario realmente ubicó productos durante esta sesión (transición de > 0 a === 0)
    // 6. No se ha ejecutado ya el auto-finish
    if (
      !isLoadingData &&
      !isLocating &&
      productsToLocate.length === 0 &&
      hadProductsInitiallyRef.current !== null &&
      hadProductsInitiallyRef.current > 0 &&
      !hasAutoFinishedRef.current
    ) {
      hasAutoFinishedRef.current = true;
      handleFinishLocated();
      // Navegar después de finalizar (las ubicaciones ya se hicieron línea por línea)
      setTimeout(() => {
        navigate('/returned', { replace: true });
      }, 1500); // Esperar 1.5 segundos para que el usuario vea el toast
    }
  }, [
    isLoadingData,
    isLocating,
    productsToLocate.length,
    handleFinishLocated,
    navigate,
  ]);

  // Si está cargando y no hay productos, mostrar spinner
  if (isLoadingData && processedProducts.length === 0) {
    return <LoadingState />;
  }

  // Si no hay productos para ubicar (y ya terminó de cargar), mostrar mensaje específico
  if (processedProducts.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-title mb-2">
            {t('ui.no_products_to_locate')}
          </h2>
          <p className="text-complementary-text mb-4">
            {t('ui.no_products_to_locate_description')}
          </p>
          <button 
            onClick={() => navigate('/returned', { replace: true })}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            {t('ui.back_to_returned')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Botones de acción */}
      <LocateActionButtons
        onBack={() => navigate('/returned', { replace: true })}
        isBackDisabled={isLocating}
      />
      <div className={`flex flex-col md:flex-row sm:justify-between items-start h-full transition-opacity duration-300 ${isLoadingData ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
        {/* Columna Izquierda - Productos por ubicar o mensaje de finalización */}
        <div className="w-full md:w-[50%] flex flex-col gap-5 p-4 sm:h-full sm:border-r sm:border-card-border">
          {productsToLocate.length === 0 ? (
            <LocateFinishedMessage
              onFinish={() => {
                handleFinishLocated();
                // Navegar después de finalizar
                setTimeout(() => {
                  navigate('/returned', { replace: true });
                }, 1500); // Esperar 1.5 segundos para que el usuario vea el toast
              }}
            />
          ) : (
            <>
              {/* Breadcrumb - Navegación */}
              <Breadcrumb
                returnedId={returnedId}
                code="REU-2501-1"
                status={2}
              />

              {/* Sección de escaneo de ubicación */}
              <ScanLocationSection
                onLocationScan={handleLocationScan}
                onScanUnits={handleScanUnits}
                onClearLocation={handleClearLocation}
                currentLocation={currentLocation}
                isValidating={isValidatingLocation}
                productBeingLocated={productBeingLocated}
                quantityToLocate={quantityToLocate}
                onQuantityChange={setQuantityToLocate}
                onConfirmLocate={async () => {
                  if (productBeingLocated && currentLocation) {
                    await handleProductLocate(
                      productBeingLocated.id,
                      currentLocation.id,
                      quantityToLocate,
                      productBeingLocated.code
                    );
                  }
                }}
                isLocating={isLocating}
              />

              {/* Header - Productos por ubicar */}
              <ProductsToLocateHeader
                products={filteredProductsToLocate}
                currentSort={toLocateSortBy}
                onSort={handleToLocateSort}
                searchQuery={toLocateSearch}
                onSearchChange={setToLocateSearch}
              />

              {/* Lista de productos por ubicar */}
              <ProductsToLocateSection
                products={filteredProductsToLocate}
                onProductLocate={handleProductLocate}
                productLocationMap={productLocationMap}
                searchQuery={toLocateSearch}
              />
            </>
          )}
        </div>

        {/* Columna Derecha - Productos ubicados */}
        <LocatedProductsSection
          products={filteredLocatedProducts}
          onSort={handleLocatedSort}
          currentSort={locatedSortBy}
          searchQuery={locatedSearch}
          onSearchChange={setLocatedSearch}
        />
      </div>
    </>
  );
};
